/**
 * OpenalexPreviewPanel
 *
 * Preview results panel for the OpenAlex retrieval modal (Panel 2).
 * Shows work count, duration estimate, sample works, KB destination selector,
 * optional download limit, and a single "download" action (metadata + abstract).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type OpenAlexPreviewResult } from '../services/openalexRetrievalService';
import { formatDuration } from '../services/opsRetrievalService';
import type { KnowledgeBase } from '../services/knowledgeBaseService';
import { TextInput } from './OpsRetrievalFormParts';

export interface OpenalexPreviewPanelProps {
  previewResult: OpenAlexPreviewResult;
  samplesLoading?: boolean;
  samplesError?: string | null;
  kbs: KnowledgeBase[];
  kbsLoading: boolean;
  selectedKbId: string;
  setSelectedKbId: (id: string) => void;
  newKbName: string;
  setNewKbName: (name: string) => void;
  kbReady: boolean;
  launching: boolean;
  launchError: string | null;
  onModify: () => void;
  /** Called with the effective download limit (null = no limit). */
  onLaunch: (limit: number | null) => void;
}

export default function OpenalexPreviewPanel({
  previewResult,
  samplesLoading = false,
  samplesError = null,
  kbs, kbsLoading, selectedKbId, setSelectedKbId, newKbName, setNewKbName, kbReady,
  launching, launchError,
  onModify, onLaunch,
}: OpenalexPreviewPanelProps) {
  const { t } = useTranslation();
  const { total_count, effective_count, sample, filter, term_count, warnings, estimated_seconds } = previewResult;

  const [limitInput, setLimitInput] = useState('');
  const maxAvailable = effective_count;

  const parsedLimit = (): number | null => {
    const v = parseInt(limitInput, 10);
    if (isNaN(v) || v <= 0) return null;
    return Math.min(v, maxAvailable);
  };

  const effectiveLimit = parsedLimit();
  const displayCount = effectiveLimit ?? maxAvailable;
  const scaledSec = maxAvailable > 0 ? Math.max(1, Math.round((displayCount / maxAvailable) * estimated_seconds)) : estimated_seconds;

  return (
    <>
      <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4 text-sm">

        {/* Count + term badge */}
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-3xl font-bold text-slate-800">{total_count.toLocaleString()}</span>
          <span className="text-slate-500">{t('openalexRetrieval.preview.worksFound', 'works found')}</span>
          <span className="text-xs text-slate-400 bg-slate-100 rounded px-2 py-0.5">
            {term_count} {t('openalexRetrieval.preview.terms', 'terms')}
          </span>
        </div>

        {/* Warnings */}
        {warnings.map((w, i) => (
          <div key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">{w}</div>
        ))}

        {/* Duration estimate */}
        <div className="border border-slate-200 rounded p-3">
          <p className="text-xs text-slate-500 mb-1">{t('openalexRetrieval.preview.mode', 'Metadata + abstract')}</p>
          <p className="text-base font-semibold text-green-700">~{formatDuration(scaledSec)}</p>
          <p className="text-xs text-slate-400">{displayCount.toLocaleString()} {t('openalexRetrieval.preview.works', 'works')}</p>
        </div>

        {/* Download limit */}
        <div className="border border-slate-200 rounded p-3 bg-slate-50">
          <p className="text-xs font-semibold text-slate-600 mb-2">
            {t('openalexRetrieval.preview.limitTitle', 'Download limit (optional)')}
          </p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={maxAvailable}
              placeholder={t('openalexRetrieval.preview.limitPlaceholder', 'e.g. 50  (blank = all {{n}})', { n: maxAvailable.toLocaleString() })}
              value={limitInput}
              onChange={(e) => setLimitInput(e.target.value)}
              className="w-48 border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
            />
            {effectiveLimit != null && (
              <span className="text-xs text-blue-700 font-medium">
                {t('openalexRetrieval.preview.limitActive', 'Will download first {{n}} works', { n: effectiveLimit.toLocaleString() })}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">
            {t('openalexRetrieval.preview.limitHint', 'Leave blank to download all available works (up to {{n}}).', { n: maxAvailable.toLocaleString() })}
          </p>
        </div>

        {/* Filter (collapsible) */}
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
            {t('openalexRetrieval.preview.filterLabel', 'OpenAlex filter')}
          </summary>
          <pre className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded font-mono whitespace-pre-wrap break-all text-slate-600">{filter}</pre>
        </details>

        {/* Sample rows */}
        {(sample.length > 0 || samplesLoading) && (
          <details className="text-xs" open>
            <summary className="cursor-pointer text-slate-500 hover:text-slate-700 mb-1">
              {t('openalexRetrieval.preview.sampleLabel', 'Sample results (first 5)')}
              {samplesLoading && (
                <span className="ml-2 inline-flex items-center gap-1 text-slate-400">
                  <span className="w-2.5 h-2.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  {t('openalexRetrieval.preview.loadingSamples', 'Loading...')}
                </span>
              )}
            </summary>
            <div className="space-y-1.5 mt-1">
              {sample.map((s) => (
                <div key={s.work_id} className="border border-slate-200 rounded p-2 bg-white">
                  <p className="font-medium text-slate-700">{s.title}</p>
                  <p className="text-slate-500">
                    {(s.authors ?? []).slice(0, 4).join(', ')}{(s.authors?.length ?? 0) > 4 ? ' et al.' : ''}
                    {s.year ? ` · ${s.year}` : ''}
                    {s.journal ? ` · ${s.journal}` : ''}
                    {typeof s.cited_by_count === 'number' ? ` · ${s.cited_by_count} ${t('openalexRetrieval.preview.citations', 'cit.')}` : ''}
                  </p>
                </div>
              ))}
            </div>
          </details>
        )}

        {samplesError && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
            {t('openalexRetrieval.preview.samplesError', 'Samples unavailable')}: {samplesError}
          </p>
        )}

        {/* KB destination */}
        <div className="border border-slate-200 rounded p-3 bg-slate-50">
          <p className="text-xs font-semibold text-slate-600 mb-2">
            {t('openalexRetrieval.preview.kbDestination', 'Destination Knowledge Base')}
          </p>
          {kbsLoading ? (
            <p className="text-xs text-slate-400">{t('common.loading', 'Loading…')}</p>
          ) : (
            <>
              <select
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                value={selectedKbId}
                onChange={(e) => {
                  setSelectedKbId(e.target.value);
                  if (e.target.value !== '__new__') setNewKbName('');
                }}
              >
                <option value="__new__">{t('openalexRetrieval.preview.newKb', '+ New Knowledge Base…')}</option>
                {kbs.map((kb) => (
                  <option key={kb.id} value={kb.id}>{kb.name}</option>
                ))}
              </select>
              {selectedKbId === '__new__' && (
                <div className="mt-2">
                  <TextInput
                    placeholder={t('openalexRetrieval.preview.newKbPlaceholder', 'Knowledge Base name…')}
                    value={newKbName}
                    onChange={(e) => setNewKbName(e.target.value)}
                    autoFocus
                  />
                </div>
              )}
            </>
          )}
        </div>

        {launchError && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{launchError}</p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 flex-shrink-0">
        <button
          type="button"
          className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex-shrink-0"
          onClick={onModify}
        >
          {t('openalexRetrieval.preview.modifyQuery', 'Modify query')}
        </button>
        <button
          type="button"
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          disabled={launching || effective_count === 0 || !kbReady}
          onClick={() => onLaunch(effectiveLimit)}
          title={`~${formatDuration(scaledSec)}`}
        >
          {launching
            ? t('openalexRetrieval.preview.launching', 'Starting…')
            : `${t('openalexRetrieval.preview.download', 'Download')} (~${formatDuration(scaledSec)})`}
        </button>
      </div>
    </>
  );
}
